const placeholderRegex = /([$!=])\{([^}]+)\}/g;

function getPlaceholderTypeMap(availablePlaceholders = []) {
  const placeholderTypeMap = {};
  const placeholdersList = availablePlaceholders[0]?.placeholders || availablePlaceholders;

  placeholdersList.forEach(p => {
    if (p.key) {
      placeholderTypeMap[p.key] = p.type || 'text';
    }
  });

  return placeholderTypeMap;
}

function createPlaceholderSpan(doc, prefix, key, placeholderTypeMap) {
  const span = doc.createElement('span');
  const type = placeholderTypeMap[key] || 'text';
  const isValid = Object.keys(placeholderTypeMap).length === 0 || key in placeholderTypeMap;

  span.setAttribute('data-placeholder-key', key);
  span.setAttribute('data-placeholder-prefix', prefix);
  span.setAttribute('data-placeholder-type', type);
  span.setAttribute('data-placeholder-valid', String(isValid));
  span.className = 'placeholder-chip';
  span.textContent = `${prefix}{${key}}`;

  return span;
}

function convertTextNode(textNode, placeholderTypeMap) {
  const text = textNode.nodeValue;
  if (!text || !/[$!=]\{[^}]+\}/.test(text)) return;

  placeholderRegex.lastIndex = 0;
  const fragment = textNode.ownerDocument.createDocumentFragment();
  let lastIndex = 0;

  text.replace(placeholderRegex, (match, prefix, key, index) => {
    if (index > lastIndex) {
      fragment.appendChild(textNode.ownerDocument.createTextNode(text.slice(lastIndex, index)));
    }
    fragment.appendChild(createPlaceholderSpan(textNode.ownerDocument, prefix, key, placeholderTypeMap));
    lastIndex = index + match.length;
    return match;
  });

  if (lastIndex < text.length) {
    fragment.appendChild(textNode.ownerDocument.createTextNode(text.slice(lastIndex)));
  }

  textNode.parentNode.replaceChild(fragment, textNode);
}

function shouldSkipTextNode(textNode) {
  const parent = textNode.parentElement;
  if (!parent) return true;

  return Boolean(parent.closest('a, [data-placeholder-key], script, style, textarea'));
}

function extractPlaceholder(text = '') {
  const match = text.match(/[$!=]\{[^}]+\}/);
  return match?.[0] || '';
}

function repairCorruptedPlaceholderLink(anchor) {
  const href = anchor.getAttribute('href') || '';
  const text = anchor.textContent || '';
  const textPlaceholder = extractPlaceholder(text);

  if (!href.includes('data-placeholder-key') || !textPlaceholder) {
    return '';
  }

  anchor.textContent = text.replace(/^[$!=]\{[^}]+\}["']?\s*[^>]*>\s*/, '');
  return textPlaceholder;
}

function normalizePlaceholderLinks(root) {
  root.querySelectorAll('a').forEach(anchor => {
    const href = anchor.getAttribute('href') || '';
    const repairedHref = repairCorruptedPlaceholderLink(anchor);
    const hrefPlaceholder = extractPlaceholder(href);
    const isLegacyPlaceholderLink = Boolean(
      repairedHref ||
      hrefPlaceholder ||
      anchor.classList.contains('placeholder-chip') ||
      href.includes('data-placeholder-key')
    );

    if (!isLegacyPlaceholderLink) return;

    if (repairedHref) {
      anchor.setAttribute('href', repairedHref);
    }

    anchor.removeAttribute('class');
    anchor.removeAttribute('style');
    Array.from(anchor.attributes).forEach(attr => {
      if (attr.name.startsWith('data-placeholder-')) {
        anchor.removeAttribute(attr.name);
      }
    });
  });
}

function convertPlainTextPlaceholders(text, availablePlaceholders) {
  const placeholderTypeMap = getPlaceholderTypeMap(availablePlaceholders);
  placeholderRegex.lastIndex = 0;

  return text.replace(placeholderRegex, (match, prefix, key) => {
    const type = placeholderTypeMap[key] || 'text';
    const isValid = Object.keys(placeholderTypeMap).length === 0 || key in placeholderTypeMap;

    return `<span data-placeholder-key="${key}" data-placeholder-prefix="${prefix}" data-placeholder-type="${type}" data-placeholder-valid="${isValid}" class="placeholder-chip">${prefix}{${key}}</span>`;
  });
}

/**
 * Converts legacy plain-text placeholders to TipTap nodes without touching
 * placeholders inside HTML attributes. Placeholder-backed legacy Jodit buttons
 * are normalized to ordinary text links.
 */
export function convertPlaceholderTextToNodes(html, availablePlaceholders = []) {
  if (!html || typeof html !== 'string') return html;

  if (typeof document === 'undefined') {
    return html.includes('<') ? html : convertPlainTextPlaceholders(html, availablePlaceholders);
  }

  const placeholderTypeMap = getPlaceholderTypeMap(availablePlaceholders);
  const template = document.createElement('template');
  template.innerHTML = html;

  normalizePlaceholderLinks(template.content);

  const showText = document.defaultView?.NodeFilter?.SHOW_TEXT || 4;
  const walker = document.createTreeWalker(template.content, showText);
  const textNodes = [];
  let currentNode = walker.nextNode();

  while (currentNode) {
    if (!shouldSkipTextNode(currentNode)) {
      textNodes.push(currentNode);
    }
    currentNode = walker.nextNode();
  }

  textNodes.forEach(textNode => convertTextNode(textNode, placeholderTypeMap));

  return template.innerHTML;
}
